import { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@breathelife/mui';
import { ApplicationSearchMode, LeadStatus, LeadStatusEnum } from '@breathelife/types';

import { SearchTextField } from '../../../../Components/TextInputs';
import { getCustomizedOptionsFromEnum } from '../../../../Helpers/options';
import { useCarrierContext } from '../../../../Hooks';
import { StyledSearchContainer, StyledSelect } from './Styles';
import { ApplicationSupportApplicationsContext } from './ApplicationsContextProvider';

const leadStatusThatApplyToApplication = [
  LeadStatusEnum.new,
  LeadStatusEnum.transactionFlowStarted,
  LeadStatusEnum.transactionFlowCompleted,
  LeadStatusEnum.qualified,
  LeadStatusEnum.submissionStarted,
  LeadStatusEnum.submissionCompleted,
  LeadStatusEnum.submissionManuallyCompleted,
];

export function ApplicationsTableHeader({
  onHasSearchQueryChange,
}: {
  onHasSearchQueryChange: (value: boolean) => void;
}): ReactElement {
  const { onSearchChange, onFilterChange, onSearchModeChange, currentSearchMode, currentStatusFilter, currentSearch } =
    useContext(ApplicationSupportApplicationsContext);
  const { t } = useTranslation();
  const { leadStatuses } = useCarrierContext();
  const [selectedStatus, setSelectedStatus] = useState<LeadStatus | ''>(currentStatusFilter || '');
  const [selectedSearchMode, setSelectedSearchMode] = useState<ApplicationSearchMode>(
    currentSearchMode || ApplicationSearchMode.application,
  );

  const onStatusFilterSelected = useCallback(
    (value: LeadStatus | '') => {
      setSelectedStatus(value);
      if (!value) {
        onFilterChange(undefined);
        return;
      }

      onFilterChange({
        lead: {
          status: value,
        },
      });
    },
    [selectedStatus],
  );

  const onSearchModeChanged = useCallback(
    (value: ApplicationSearchMode) => {
      setSelectedSearchMode(value);
      onSearchModeChange(value);
    },
    [onSearchModeChange],
  );

  const searchModeOptionsFromEnum = useMemo(
    () =>
      getCustomizedOptionsFromEnum<ApplicationSearchMode>(ApplicationSearchMode, {
        translationBasePath: 'applicationTable.applicationSearchModes',
        customizationMap: Object.keys(ApplicationSearchMode).reduce(
          (customizationMap, searchMode) => ({ ...customizationMap, [searchMode]: { label: searchMode } }),
          {},
        ),
      }),
    [],
  );

  const selectOptionsFromEnum = useMemo(
    () =>
      getCustomizedOptionsFromEnum<LeadStatus>(LeadStatusEnum, {
        translationBasePath: 'leadStatuses',
        customizationMap: leadStatuses,
        withDefaultOption: true,
        defaultOptionLabel: t('leadsListTable.status'),
        whitelist: leadStatusThatApplyToApplication,
      }),
    [leadStatuses],
  );

  return (
    <Box display='flex'>
      <StyledSearchContainer>
        <SearchTextField
          defaultValue={currentSearch}
          onSearchChange={(value) => {
            onSearchChange(value);
            onHasSearchQueryChange(!!value);
          }}
          label={t(`applicationTable.search.${selectedSearchMode}`)}
          fullWidth
        />
      </StyledSearchContainer>
      <StyledSelect
        isSearchFilter
        id='application-search-mode-select'
        value={selectedSearchMode}
        onChange={onSearchModeChanged}
        options={searchModeOptionsFromEnum}
        label={t('applicationTable.searchMode')}
      />
      <StyledSelect
        isSearchFilter
        id='application-lead-status-select'
        value={selectedStatus}
        onChange={onStatusFilterSelected}
        options={selectOptionsFromEnum}
        label={t('applicationTable.filterByLeadStatus')}
      />
    </Box>
  );
}
